import React, { useEffect, useState } from "react";
import { Users, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { GBP, NUM2, fmtKwh, fmtKwp, fmtDateGB } from "../../lib/format";
import { copyToClipboard } from "../../lib/clipboard";
import { AssignmentRow } from "./utils";
import LastEnrichmentChip from "../../components/LastEnrichmentChip";

/** Resolve absolute CAPEX £ using the same priority order as the backend:
 *  direct `capex_gbp` → per-kWp × systemSize → fallback default. Returns
 *  { total, source } so the tile can label how the number was derived. */
function resolveCapex(project, design) {
  const direct = project?.capex_gbp;
  if (direct != null && Number(direct) > 0) {
    return { total: Number(direct), source: "direct" };
  }
  const kwp = Number(design?.systemSize || 0);
  const perKwp = Number(project?.capex_per_kwp_gbp ?? 850);
  return { total: kwp * perKwp, source: "per_kwp" };
}

export default function Overview({ project, design, quote, tasks, monitoring, reload }) {
  const taskDone = tasks.filter(t => t.status === "done").length;
  const taskTotal = tasks.length;
  const taskProgress = taskTotal ? Math.round((taskDone / taskTotal) * 100) : 0;
  const [users, setUsers] = useState([]);
  const [portalUrl, setPortalUrl] = useState(
    project.customer_portal_token
      ? `${window.location.origin}/p/${project.customer_portal_token}` : ""
  );
  // Inline CAPEX/OPEX editor state — lets ops tweak project economics
  // without opening the Financials tab. Reads live from the project so a
  // save on the Financials tab is reflected here.
  const [capexTotal, setCapexTotal] = useState(
    project?.capex_gbp != null ? String(project.capex_gbp) : ""
  );
  const [capexPerKwp, setCapexPerKwp] = useState(
    project?.capex_per_kwp_gbp != null ? String(project.capex_per_kwp_gbp) : "850"
  );
  const [opexPerKwp, setOpexPerKwp] = useState(
    project?.opex_per_kwp_gbp != null ? String(project.opex_per_kwp_gbp) : "12"
  );
  const [savingPricing, setSavingPricing] = useState(false);
  useEffect(() => {
    setCapexTotal(project?.capex_gbp != null ? String(project.capex_gbp) : "");
    setCapexPerKwp(project?.capex_per_kwp_gbp != null ? String(project.capex_per_kwp_gbp) : "850");
    setOpexPerKwp(project?.opex_per_kwp_gbp != null ? String(project.opex_per_kwp_gbp) : "12");
  }, [project?.capex_gbp, project?.capex_per_kwp_gbp, project?.opex_per_kwp_gbp]);
  useEffect(() => { api.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setPortalUrl(project.customer_portal_token
      ? `${window.location.origin}/p/${project.customer_portal_token}` : "");
  }, [project.customer_portal_token]);

  const assign = async (field, userId) => {
    try {
      await api.patch(`/projects/${project.id}`, { [field]: userId || null });
      toast.success("Assignment updated");
      reload && reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generatePortal = async () => {
    try {
      const { data } = await api.post(`/projects/${project.id}/portal-token`);
      const url = `${window.location.origin}/p/${data.token}`;
      setPortalUrl(url);
      await copyToClipboard(url, "Customer portal link copied");
      reload && reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const findUser = (id) => users.find(u => u.id === id);

  const capex = resolveCapex(project, design);
  const sysKwp = Number(design?.systemSize || 0);
  const annualOpex = sysKwp * Number(project?.opex_per_kwp_gbp ?? 12);
  const previewCapex = (() => {
    const direct = capexTotal === "" ? null : Number(capexTotal);
    if (direct != null && direct > 0) return direct;
    return sysKwp * Number(capexPerKwp || 0);
  })();
  const previewOpex = sysKwp * Number(opexPerKwp || 0);

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        capex_gbp: capexTotal === "" ? null : Number(capexTotal),
        capex_per_kwp_gbp: Number(capexPerKwp),
        opex_per_kwp_gbp: Number(opexPerKwp),
      });
      toast.success("Project pricing saved");
      reload && reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPricing(false); }
  };

  return (
    <div className="space-y-1" data-testid="overview-grid">
      <LastEnrichmentChip projectId={project.id} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-1 bg-[var(--surface-2)]">
      <div className="bg-white p-6 border border-[var(--line)]">
        <div className="label">System</div>
        <div className="kpi-value mt-1">{fmtKwp(design?.systemSize || 0)}</div>
        <div className="text-xs muted">{design?.numberOfModules || 0} modules · {design?.inverterModel || "no inverter"}</div>
      </div>
      <div className="bg-white p-6 border border-[var(--line)]">
        <div className="label">Annual Yield</div>
        <div className="kpi-value mt-1">{fmtKwh(design?.annualEnergyProduction || 0)}</div>
        <div className="text-xs muted">{NUM2.format(design?.specificYield || 0)} kWh/kWp · PR {design?.performanceRatio || 0}</div>
      </div>
      <div className="bg-white p-6 border border-[var(--line)]" data-testid="kpi-total-capex">
        <div className="label">Total CAPEX</div>
        <div className="kpi-value mt-1">{GBP.format(Math.max(0, capex.total))}</div>
        <div className="text-xs muted">
          {capex.source === "direct"
            ? "Direct £ override"
            : `£${Number(project?.capex_per_kwp_gbp ?? 850)}/kWp × ${sysKwp.toFixed(2)} kWp`}
          {" · "}Annual OPEX {GBP.format(Math.max(0, annualOpex))}
        </div>
      </div>
      <div className="bg-white p-6 border border-[var(--line)]" data-testid="kpi-completion">
        <div className="label">Completion</div>
        <div className="kpi-value mt-1 text-lg">
          {project.handover_date
            ? fmtDateGB(project.handover_date)
            : project.expected_completion_date
              ? fmtDateGB(project.expected_completion_date)
              : "—"}
        </div>
        <div className="text-xs muted">
          {(() => {
            const baseline = project.expected_completion_baseline_date;
            const expected = project.expected_completion_date;
            const actual = project.handover_date;
            if (actual) return `Handed over · planned ${baseline ? fmtDateGB(baseline) : "—"}`;
            if (expected && baseline && expected !== baseline) {
              const days = Math.round((Date.parse(expected) - Date.parse(baseline)) / 86_400_000);
              return `Expected · slip ${days > 0 ? "+" : ""}${days}d vs baseline ${fmtDateGB(baseline)}`;
            }
            if (expected) return "Expected — baseline captured";
            return "No target date yet";
          })()}
        </div>
      </div>
      <div className="bg-white p-6 border border-[var(--line)]">
        <div className="label">Payback</div>
        <div className="kpi-value mt-1">{quote?.financials?.payback_years ? `${quote.financials.payback_years} yr` : "—"}</div>
        <div className="text-xs muted">IRR {quote?.financials?.irr_pct?.toFixed?.(2) || 0}% · NPV {GBP.format(quote?.financials?.npv_gbp || 0)}</div>
      </div>
      <div className="bg-white p-6 border border-[var(--line)]">
        <div className="label">Install Progress</div>
        <div className="kpi-value mt-1">{taskProgress}%</div>
        <div className="text-xs muted">{taskDone}/{taskTotal} tasks done</div>
      </div>
      <div className="bg-white p-6 border border-[var(--line)]">
        <div className="label">Performance (90d)</div>
        <div className="kpi-value mt-1">{monitoring?.performance_ratio_pct?.toFixed?.(1) || 0}%</div>
        <div className="text-xs muted">Actual {fmtKwh(monitoring?.total_actual_kwh || 0)} / Sim {fmtKwh(monitoring?.total_simulated_kwh || 0)}</div>
      </div>
      </div>

      {project.solaredge_design_url && (
        <div
          className="bg-white p-3 border border-[var(--line)] flex items-center gap-3 flex-wrap"
          data-testid="overview-solaredge-link"
        >
          <div className="label !text-[9px] shrink-0">Design reference</div>
          <a
            href={project.solaredge_design_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-mono truncate flex-1 hover:underline"
            style={{ color: "var(--accent-hover)" }}
            title={project.solaredge_design_url}
            data-testid="overview-solaredge-url"
          >
            {project.solaredge_design_url}
          </a>
          <a
            href={project.solaredge_design_url}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost text-xs shrink-0"
            data-testid="overview-solaredge-open"
          >
            Open ↗
          </a>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 bg-[var(--surface-2)]" data-testid="assignments-card">
        <div className="bg-white p-6 border border-[var(--line)]">
          <div className="flex items-center gap-2 mb-3"><Users size={14} /><div className="label">Team assignments</div></div>
          <div className="space-y-3">
            <AssignmentRow label="EPC / Installer" users={users.filter(u => u.role === "epc" || u.role === "admin")} value={project.assigned_epc} onChange={(v) => assign("assigned_epc", v)} testid="assign-epc" />
            <AssignmentRow label="Investor" users={users.filter(u => u.role === "investor" || u.role === "admin")} value={project.assigned_investor} onChange={(v) => assign("assigned_investor", v)} testid="assign-investor" />
            <AssignmentRow label="Project Manager" users={users.filter(u => u.role === "manager" || u.role === "admin")} value={project.assigned_manager} onChange={(v) => assign("assigned_manager", v)} testid="assign-manager" />
          </div>
        </div>
        <div className="bg-white p-6 border border-[var(--line)]" data-testid="customer-portal-card">
          <div className="label mb-3">Customer portal</div>
          {portalUrl ? (
            <>
              <div className="text-xs muted">Share this read-only link with the customer for lifetime access to live monitoring.</div>
              <div className="mt-3 p-2 bg-[var(--surface)] font-mono text-[11px] break-all" data-testid="portal-url">{portalUrl}</div>
              <div className="flex gap-2 mt-3">
                <button
                  className="btn-ghost flex-1 justify-center"
                  onClick={() => copyToClipboard(portalUrl, "Copied")}
                  data-testid="portal-copy-btn"
                >
                  <Copy size={13} /> Copy
                </button>
                <a href={portalUrl} target="_blank" rel="noreferrer" className="btn-accent flex-1 justify-center" data-testid="portal-open-btn">
                  Open ↗
                </a>
              </div>
            </>
          ) : (
            <>
              <div className="text-xs muted">Generate a shareable link to give the customer read-only access to their project and monitoring data.</div>
              <button className="btn-accent mt-4 w-full justify-center" onClick={generatePortal} data-testid="portal-generate-btn">
                Generate customer portal link
              </button>
              <div className="text-xs muted mt-2">Auto-generated when the project reaches monitoring status.</div>
            </>
          )}
        </div>
      </div>

      {/* -------- Compact CAPEX / OPEX inline editor -------- */}
      <div className="bg-white p-6 border border-[var(--line)]" data-testid="overview-pricing-card">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="label">Project inputs · CAPEX / OPEX</div>
            <div className="text-xs muted mt-1">
              CAPEX resolves as <strong>direct £ total</strong> → <strong>£/kWp × system size</strong> ({sysKwp.toFixed(2)} kWp). OPEX is <strong>£/kWp/yr × system size</strong>.
            </div>
          </div>
          <button onClick={savePricing} disabled={savingPricing}
                  className="btn-primary" data-testid="overview-save-pricing-btn">
            <Check size={13} /> {savingPricing ? "Saving…" : "Save"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <div className="label mb-1">CAPEX total (£) — direct</div>
            <input className="input-swiss w-full" type="number" step="1"
                   placeholder="blank ⇒ use £/kWp"
                   value={capexTotal}
                   onChange={(e)=>setCapexTotal(e.target.value)}
                   data-testid="overview-capex-total-input" />
          </label>
          <label className="block">
            <div className="label mb-1">CAPEX (£/kWp)</div>
            <input className="input-swiss w-full" type="number" step="1"
                   value={capexPerKwp}
                   onChange={(e)=>setCapexPerKwp(e.target.value)}
                   data-testid="overview-capex-per-kwp-input" />
          </label>
          <label className="block">
            <div className="label mb-1">OPEX (£/kWp/yr)</div>
            <input className="input-swiss w-full" type="number" step="0.5"
                   value={opexPerKwp}
                   onChange={(e)=>setOpexPerKwp(e.target.value)}
                   data-testid="overview-opex-per-kwp-input" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label">Resolved CAPEX</div>
            <div className="font-mono mt-1" data-testid="overview-resolved-capex">
              {GBP.format(Math.max(0, previewCapex))}
            </div>
          </div>
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label">Annual OPEX</div>
            <div className="font-mono mt-1" data-testid="overview-resolved-opex">
              {GBP.format(Math.max(0, previewOpex))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

